import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  FilingDocumentKind,
  PayPeriod,
  PayrollFiling,
  PayrollFilingDocument,
  Person,
} from "@somasi/shared";
import {
  FILING_AUTHORITIES,
  FILING_DOCUMENT_KINDS,
  FILING_STATUSES,
  MAX_PAGE_SIZE,
  PAY_PERIOD_STATUSES,
  PAYROLL_READ_ROLES,
  PAYROLL_WRITE_ROLES,
  payPeriodLabel,
} from "@somasi/shared";
import { api, type PayRunResult, type PayslipRow } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Calculator,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  Lock,
  Paperclip,
  Pencil,
  Play,
  Trash2,
  Unlock,
  Upload,
  X,
} from "lucide-react";
import {
  FilterSelect,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useListSort,
  usePagination,
} from "@/components/list-controls";
import {
  AttendancePersonSelect,
  PayPeriodSelect,
  invalidatePayPeriodLists,
} from "@/components/entity-select";
import { notify } from "@/lib/notify";
import { downloadPayrollReport } from "@/lib/payroll-report";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { EMPLOYMENT_LABEL } from "@/components/person-form";
import { PayslipLetterheadEditor } from "@/components/payslip-letterhead";
import {
  SettleUnansweredDialog,
  UnansweredDaysTable,
  useUnansweredDays,
} from "@/components/settle-unanswered-dialog";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/billing";
import { generatePayslipPDF, type PayslipPdfData } from "@/lib/payslip-pdf";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { cn, fileSize, formatDate } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/payslips")({
  head: () => ({
    meta: [
      { title: "Payslips — Somasi Ops" },
      {
        name: "description",
        content: "Payslips month by month, and the run that produced them.",
      },
    ],
  }),
  component: PayslipsPage,
});

/**
 * Payslips — and, for whoever runs the payroll, the running of them.
 *
 * The same page serves two readers: an employee sees their own months and
 * nobody else's, and the payroll roles see everybody's. That is the API's
 * doing, not a branch here — `GET /payslips` narrows by the caller's roles, so
 * there is no client-side filter to get wrong.
 *
 * It used to be two screens, with a separate "Run payroll" tab holding the
 * calculate/approve machinery and the statutory returns. They were the same
 * subject read from two places: the tab produced payslips you then went
 * elsewhere to look at, and the figures a month is approved on are exactly the
 * table above. Generating a month, checking it, approving it and filing the
 * returns are now one screen, in that order down the page.
 */

const STATUS_TONE: Record<string, string> = {
  open: "bg-muted text-muted-foreground",
  calculating: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200",
  review: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  approved: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  paid: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200",
  filed: "bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-200",
};

const AUTHORITY_LABEL: Record<string, string> = {
  payroll_tax: "Payroll tax",
  nhi: "National Health Insurance",
  ssb: "Social Security",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Statuses from which nothing may be recalculated — the server's `CLOSED`. */
const CLOSED = ["approved", "paid", "filed"];

const FILING_STATUS_LABEL: Record<string, string> = {
  due: "Due",
  filed: "Filed",
  paid: "Paid",
  overdue: "Overdue",
};

/** What a file attached to a return is, in the words a filing clerk uses. */
const FILING_DOCUMENT_KIND_LABEL: Record<FilingDocumentKind, string> = {
  return: "Filed return",
  payment_receipt: "Payment receipt",
  acknowledgement: "Acknowledgement",
  correspondence: "Correspondence",
  other: "Other",
};

/** Sentence case for an enum whose values are the wire format. */
const titleCase = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, " ");

/**
 * One enum filter, as every table on this page spells it.
 *
 * The empty string is "no filter" everywhere in this file, but a Radix select
 * cannot hold an empty value — so the "any" row carries a sentinel and is
 * translated on the way in and out rather than leaking into the query.
 *
 * Each of these narrows the API call rather than the rows already fetched.
 * Filtering a page of ten in the browser would hide rows from the other pages
 * and leave the footer counting a set the table no longer shows.
 */
const ANY = "__any";

function EnumFilter({
  id,
  label,
  value,
  onChange,
  options,
  anyLabel,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly { value: string; label: string }[];
  anyLabel: string;
  className?: string;
}) {
  // No visible label: the "any" option reads as the column's name while nothing is
  // picked, which is what keeps the pickers on the same row as the rest of the
  // toolbar. The label still names the trigger for a screen reader.
  return (
    <FilterSelect
      value={value || ANY}
      onChange={(next) => onChange(next === ANY ? "" : next)}
      allValue={ANY}
      className={className}
      options={[{ value: ANY, label: anyLabel }, ...options]}
    />
  );
}

function PayslipsPage() {
  const { hasAny } = useAuth();
  const qc = useQueryClient();
  const canReadAll = hasAny(PAYROLL_READ_ROLES);
  const canRun = hasAny(PAYROLL_WRITE_ROLES);
  const mine = !canReadAll;

  const [employeeId, setEmployeeId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [reporting, setReporting] = useState(false);
  /** What the last run had to say about the figures, kept until the next one. */
  const [warnings, setWarnings] = useState<string[] | null>(null);
  /*
   * What stopped the last run for want of answers, and the month it was for —
   * the month is what the settle dialog reads the open days of.
   */
  const [unanswered, setUnanswered] = useState<UnansweredRun | null>(null);
  const [settling, setSettling] = useState(false);

  const filters = { employeeId, periodId };
  /*
   * Which table is showing. A run switches to Payslips on its own: pressing
   * Calculate is asking what the month comes to, and leaving the answer on
   * another tab makes the button look like it did nothing.
   */
  const [tab, setTab] = useState("payslips");

  const pager = usePagination({ resetKey: filters });

  // The filters and the page both go to the API and both are part of the cache
  // key, so each control is a fetch rather than a re-filter of a table the
  // browser downloaded whole.
  const payslips = useQuery({
    queryKey: ["payslips", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const { data, error, count } = await api.payslips.list({
        employeeId: employeeId || undefined,
        payPeriodId: periodId || undefined,
        page: pager.page,
        pageSize: pager.pageSize,
      });
      if (error) throw new Error(error.message);
      return { rows: data ?? [], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const rows = payslips.data?.rows ?? [];
  const filtered = Boolean(employeeId || periodId);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["payslips"] });
    void qc.invalidateQueries({ queryKey: ["pay_periods"] });
    void qc.invalidateQueries({ queryKey: ["payroll_filings"] });
    // What the generate dialog read about a month: whether it exists at all and
    // what state it is in are both exactly what running it has just changed.
    void qc.invalidateQueries({ queryKey: ["pay_period_for"] });
    invalidatePayPeriodLists(qc);
  };

  /*
   * The payslips table itself, built once.
   *
   * It is the whole of what an employee sees and the first of three tabs for
   * everybody else, so it is held here rather than written out in both
   * branches — two copies of a table is two places to fix a column.
   */
  const payslipsTable = payslips.error ? (
    <Alert variant="destructive">
      <AlertDescription>{(payslips.error as Error).message}</AlertDescription>
    </Alert>
  ) : (
    /*
     * The rows scroll inside the card — sideways on a narrow screen, and
     * downwards once there are more of them than the viewport holds — while
     * the headings stay pinned above them and the pager sits below. Both used
     * to live inside the scroller, so the columns went unlabelled and the page
     * controls were carried off the moment anybody scrolled, which is exactly
     * when a long list needs them.
     */
    <ListPageTable>
      <Table containerClassName="min-h-0 flex-1">
        <TableHeader sticky>
          {/*
           * Not sortable. The API cuts its pages in one fixed order — newest month
           * first, then by name — and takes no sort parameter, so a heading that
           * sorted would only be reordering the ten rows on screen.
           */}
          <TableRow>
            <TableHead className="w-8" />
            <TableHead>Month</TableHead>
            {!mine && <TableHead>Employee</TableHead>}
            <TableHead className="text-right">Gross</TableHead>
            <TableHead className="text-right">Deductions</TableHead>
            <TableHead className="text-right">Net</TableHead>
            <TableHead className="text-right">YTD gross</TableHead>
            <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payslips.isLoading ? (
            <TableMessageRow colSpan={mine ? 7 : 8}>Loading…</TableMessageRow>
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={mine ? 7 : 8}>
              <p className="font-medium text-foreground">
                {filtered ? "No payslips match these filters." : "No payslips yet."}
              </p>
              <p className="mx-auto mt-1 max-w-lg text-sm">
                {filtered
                  ? "Clear the filters to see every month."
                  : canRun
                    ? "Press Generate payroll to work out a month: it reads the attendance grid, " +
                      "applies the contracts and rates, and writes a payslip each."
                    : "A payslip appears here once a pay period has been calculated."}
              </p>
            </TableMessageRow>
          ) : (
            rows.map((slip) => <PayslipRows key={slip.id} slip={slip} mine={mine} />)
          )}
        </TableBody>
      </Table>

      <TablePagination
        pagination={pager}
        total={payslips.data?.total}
        loading={payslips.isLoading}
        unit="payslip"
      />
    </ListPageTable>
  );

  return (
    <ListPage>
      <ListPageHeader className="space-y-6 pb-4">
        <PageHeader
          title="Payslips"
          description={
            mine
              ? "Your pay month by month, and the receipt for each payment."
              : "Every payslip issued, the month it came from, and the returns it owes."
          }
          actions={
            canReadAll || canRun ? (
              <div className="flex flex-wrap gap-2">
                {canReadAll && (
                  <Button variant="outline" onClick={() => setReporting(true)}>
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Payroll report
                  </Button>
                )}
                {canRun && (
                  <Button onClick={() => setGenerating(true)}>
                    <Play className="mr-2 h-4 w-4" />
                    Generate payroll
                  </Button>
                )}
              </div>
            ) : undefined
          }
        />

        {unanswered && (
          <Alert variant="destructive">
            <HelpCircle className="h-4 w-4" />
            <AlertTitle>Some days have not been answered</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{unanswered.message}</p>
              <p className="text-sm">
                A day with no record is not the same as a day somebody was absent, so the run will
                not guess. Mark them here a stretch at a time, settle them on the{" "}
                <strong>Attendance</strong> grid, or run <strong>Fill in from logins</strong> there
                first.
              </p>
              {canRun && (
                <Button size="sm" variant="outline" onClick={() => setSettling(true)}>
                  Mark these days
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {warnings && warnings.length > 0 && (
          <Alert>
            <AlertTitle>Worth a look before approving</AlertTitle>
            <AlertDescription>
              <ul className="list-inside list-disc space-y-1">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </ListPageHeader>

      {/*
       * Three tables, three tabs.
       *
       * They were stacked down one page, in the order the work is done:
       * generate a month, read the payslips, file the returns. That reads well
       * as a description and badly as a screen — each table pages and filters
       * independently, so the two you are not using sit between you and the
       * one you are, and the page scrolls past three footers to reach the
       * bottom.
       *
       * An employee sees no tab bar at all. Their own payslips are the only
       * table they may read, and a single tab is a label pretending to be a
       * choice.
       */}
      {canReadAll ? (
        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col gap-4">
          <TabsList className="shrink-0 self-start">
            <TabsTrigger value="payslips">Payslips</TabsTrigger>
            <TabsTrigger value="months">Months</TabsTrigger>
            <TabsTrigger value="returns">Returns</TabsTrigger>
          </TabsList>

          <TabsContent value="payslips" className="mt-0 flex min-h-0 flex-1 flex-col gap-4">
            {/* No search box: the API has no text search over payslips, and the
                employee picker is the search a payslip is found by. */}
            <ListToolbar className="mb-0 shrink-0">
              <AttendancePersonSelect
                id="payslip-employee"
                value={employeeId}
                onChange={setEmployeeId}
                clearable
                clearLabel="Everybody"
                placeholder="Everybody"
                className="w-full sm:w-72"
              />
              <PayPeriodSelect
                id="payslip-month"
                value={periodId}
                onChange={setPeriodId}
                clearable
                clearLabel="Any month"
                className="w-full sm:w-56"
              />
              {filtered && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEmployeeId("");
                    setPeriodId("");
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              )}
            </ListToolbar>
            {payslipsTable}
          </TabsContent>

          <TabsContent value="months" className="mt-0 flex min-h-0 flex-1 flex-col">
            <MonthsSection
              canRun={canRun}
              selectedId={periodId}
              onSelect={setPeriodId}
              onRan={(run) => {
                setWarnings(run.warnings);
                setUnanswered(null);
                setTab("payslips");
                refresh();
              }}
              onUnanswered={(run) => {
                setUnanswered(run);
                setWarnings(null);
              }}
              onChanged={refresh}
            />
          </TabsContent>

          <TabsContent value="returns" className="mt-0 flex min-h-0 flex-1 flex-col">
            <ReturnsSection periodId={periodId} onSelectPeriod={setPeriodId} />
          </TabsContent>
        </Tabs>
      ) : (
        payslipsTable
      )}

      {settling && unanswered && (
        <SettleUnansweredDialog
          from={unanswered.from}
          to={unanswered.to}
          label={unanswered.label}
          onClose={() => setSettling(false)}
        />
      )}

      {reporting && (
        <PayrollReportDialog employeeId={employeeId} onClose={() => setReporting(false)} />
      )}

      {generating && (
        <GenerateDialog
          onClose={() => setGenerating(false)}
          onRan={(run) => {
            setGenerating(false);
            setWarnings(run.warnings);
            setUnanswered(null);
            // Opened on what was just produced, so the table shown is the run.
            setPeriodId(run.period.id);
            setTab("payslips");
            refresh();
          }}
        />
      )}
    </ListPage>
  );
}

/**
 * One payslip: the summary row, and the breakdown underneath when it is opened.
 *
 * The breakdown is the document's own — the same lines, in the same wording,
 * with the same percentages beside each deduction — because it is fetched from
 * the same route the PDF is built from. Restating those labels here would be a
 * second place for the rates to be described, and the two would drift.
 */
function PayslipRows({ slip, mine }: { slip: PayslipRow; mine: boolean }) {
  const [open, setOpen] = useState(false);
  const columns = mine ? 7 : 8;

  return (
    <Fragment>
      <TableRow>
        <TableCell className="p-0 pl-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} the ${slip.period_label ?? ""} breakdown`}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium tabular-nums">{slip.period_label ?? "—"}</TableCell>
        {!mine && <TableCell>{slip.employee_name}</TableCell>}
        <TableCell className="text-right tabular-nums">{money(slip.gross)}</TableCell>
        <TableCell className="text-right tabular-nums">
          {money(slip.employee_deductions_total)}
        </TableCell>
        <TableCell className="text-right font-semibold tabular-nums">{money(slip.net)}</TableCell>
        <TableCell className="text-muted-foreground text-right tabular-nums">
          {money(slip.ytd_gross)}
        </TableCell>
        <TableCell>
          <RowActions>
            <PayslipDownload payslipId={slip.id} label={slip.period_label} />
          </RowActions>
        </TableCell>
      </TableRow>

      {open && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell />
          <TableCell colSpan={columns - 1} className="py-4">
            <PayslipBreakdown payslipId={slip.id} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

/** Everything the printed payslip says, read from the route that prints it. */
function PayslipBreakdown({ payslipId }: { payslipId: string }) {
  const document = usePayslipDocument(payslipId);

  if (document.isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (document.error) {
    return <p className="text-destructive text-sm">{(document.error as Error).message}</p>;
  }

  const data = document.data;
  if (!data) return null;

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <LineBlock
        title="Earnings"
        lines={data.earnings}
        total={data.total_earnings}
        totalLabel="Total earnings"
      />
      <LineBlock
        title="Deductions"
        lines={data.employee_deductions}
        total={data.total_deductions}
        totalLabel="Total deductions"
      />
      <div className="space-y-4">
        <LineBlock title="Paid by Somasi" lines={data.employer_contributions} />
        {data.days && (
          <div className="space-y-1">
            <h4 className="text-xs font-semibold uppercase tracking-wide">Days</h4>
            <dl className="space-y-1 text-sm">
              <Figure label="Working" value={String(data.days.working)} />
              <Figure label="Paid" value={String(data.days.paid)} />
              <Figure label="Leave" value={String(data.days.leave)} />
              <Figure label="Unpaid" value={String(data.days.unpaid)} />
            </dl>
          </div>
        )}
        <div className="space-y-1">
          <h4 className="text-xs font-semibold uppercase tracking-wide">Year to date</h4>
          <dl className="space-y-1 text-sm">
            <Figure label="Gross" value={money(data.ytd_gross)} />
            <Figure label="Taxed" value={money(data.ytd_taxed)} />
            <Figure label="Untaxed" value={money(data.ytd_untaxed)} />
            <Figure label="Tax paid" value={money(data.ytd_tax_paid)} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function LineBlock({
  title,
  lines,
  total,
  totalLabel,
}: {
  title: string;
  lines: { label: string; amount: number | null }[];
  total?: number;
  totalLabel?: string;
}) {
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-semibold uppercase tracking-wide">{title}</h4>
      <dl className="space-y-1 text-sm">
        {lines.length === 0 ? (
          <p className="text-muted-foreground">Nothing.</p>
        ) : (
          lines.map((line) => (
            // A nil line prints as a dash on the document rather than as zero,
            // and reads the same way here.
            <Figure
              key={line.label}
              label={line.label}
              value={line.amount == null ? "—" : money(line.amount)}
            />
          ))
        )}
        {total != null && (
          <div className="flex justify-between gap-4 border-t pt-1 font-semibold">
            <dt>{totalLabel}</dt>
            <dd className="tabular-nums">{money(total)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * The document behind one payslip.
 *
 * Fetched per payslip rather than with the list: it joins the employee's
 * statutory identifiers and the period's rates, which is work nobody asked for
 * on the twenty rows they are not looking at. A period of forty people would
 * otherwise be forty joins to draw one page, and most payslips are never
 * opened.
 *
 * One query serves both readers of it — the breakdown below the row and the
 * download beside it — so opening a payslip and then printing it is one request
 * rather than two. The stored snapshot never changes, hence `staleTime`.
 */
function payslipDocumentQuery(payslipId: string) {
  return {
    queryKey: ["payslip_document", payslipId],
    staleTime: Infinity,
    queryFn: async (): Promise<PayslipPdfData> => {
      const result = await api.payslips.document(payslipId);
      if (result.error) throw new Error(result.error.message);
      return result.data as PayslipPdfData;
    },
  };
}

function usePayslipDocument(payslipId: string) {
  return useQuery(payslipDocumentQuery(payslipId));
}

/** Downloads one payslip, fetching the document only if it is not in hand. */
function PayslipDownload({ payslipId, label }: { payslipId: string; label?: string | null }) {
  const qc = useQueryClient();

  const download = useMutation({
    mutationFn: () => qc.fetchQuery(payslipDocumentQuery(payslipId)),
    onSuccess: (data) => {
      if (data) generatePayslipPDF(data);
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <RowAction
      label={label ? `Download the ${label} payslip` : "Download payslip"}
      onClick={() => download.mutate()}
      disabled={download.isPending}
    >
      <Download />
    </RowAction>
  );
}

/**
 * The months, and what may still be done to each.
 *
 * Calculate, approve, reopen — the status machine that was the whole of the old
 * Run payroll tab. Approving is what locks the month: the attendance days behind
 * it stop being editable, and from then on a correction lands on the next open
 * period rather than rewriting a figure that has been reported.
 */
function MonthsSection({
  canRun,
  selectedId,
  onSelect,
  onRan,
  onUnanswered,
  onChanged,
}: {
  canRun: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
  onRan: (run: PayRunResult) => void;
  onUnanswered: (run: UnansweredRun) => void;
  onChanged: () => void;
}) {
  const [reopening, setReopening] = useState<PayPeriod | null>(null);
  const [status, setStatus] = useState("");
  const [year, setYear] = useState("");
  const sort = useListSort({ column: "period_start", ascending: false });
  const filters = { status, year, sort: sort.sort };
  const pager = usePagination({ resetKey: filters });

  /*
   * The years to offer. Read from the periods themselves rather than counted
   * forward from a start date: a year with no period in it is a year with
   * nothing to show, and offering it would be a filter that can only ever
   * empty the table.
   */
  const years = useQuery({
    queryKey: ["pay_period_years"],
    queryFn: async () => {
      const { data, error } = await api.payPeriods.list().select("year").page(1, MAX_PAGE_SIZE);
      if (error) throw new Error(error.message);
      const seen = new Set((data ?? []).map((row) => Number((row as { year: number }).year)));
      return [...seen].filter(Number.isFinite).sort((a, b) => b - a);
    },
  });

  const periods = useQuery({
    queryKey: ["pay_periods", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const list = api.payPeriods
        .list()
        .order(sort.sort.column, { ascending: sort.sort.ascending });
      // Many months share a status, so they fall back to newest first.
      if (sort.sort.column !== "period_start") list.order("period_start", { ascending: false });
      if (status) list.eq("status", status);
      if (year) list.eq("year", Number(year));
      const { data, error, count } = await list.page(pager.page, pager.pageSize);
      if (error) throw new Error(error.message);
      return { rows: (data ?? []) as PayPeriod[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const calculate = useMutation({
    mutationFn: async (period: PayPeriod) => {
      const result = await api.payPeriods.calculate(period.id);
      if (result.error) throw runError(result.error);
      return result.data as PayRunResult;
    },
    onSuccess: (run) => {
      onSelect(run.period.id);
      onRan(run);
    },
    onError: (error: Error & { code?: string }, period) => {
      // The server refuses the whole run while any half-day is unanswered, and
      // says so in the message rather than leaving somebody hunting the grid.
      if (error.code === "unanswered_days") {
        return onUnanswered({
          message: error.message,
          from: period.period_start,
          to: period.period_end,
          label: period.label,
        });
      }
      notify.error(error.message);
    },
  });

  const approve = useMutation({
    mutationFn: async (period: PayPeriod) => {
      const result = await api.payPeriods.approve(period.id);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Period approved. Its attendance days are now locked.");
      onChanged();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const rows = periods.data?.rows ?? [];

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-2">
      <h2 className="shrink-0 text-sm font-semibold">Months</h2>
      <p className="text-muted-foreground shrink-0 text-sm">
        Calculating writes a payslip each from the attendance grid and can be repeated. Approving
        locks the month, after which the days behind it can no longer change.
      </p>

      <ListToolbar className="mb-2 shrink-0">
        <EnumFilter
          id="month-status"
          label="Status"
          value={status}
          onChange={setStatus}
          anyLabel="Any status"
          options={PAY_PERIOD_STATUSES.map((value) => ({ value, label: titleCase(value) }))}
          className="w-full sm:w-44"
        />
        <EnumFilter
          id="month-year"
          label="Year"
          value={year}
          onChange={setYear}
          anyLabel="Every year"
          options={(years.data ?? []).map((value) => ({
            value: String(value),
            label: String(value),
          }))}
          className="w-full sm:w-32"
        />
        {(status || year) && (
          <Button
            variant="ghost"
            onClick={() => {
              setStatus("");
              setYear("");
            }}
          >
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        )}
      </ListToolbar>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {/* The label is words ("March 2026"), so the month sorts by its start. */}
              <SortHeader sort={sort} column="period_start">
                Month
              </SortHeader>
              {/* The same order as Month, so a second heading for it would be noise. */}
              <TableHead>Dates</TableHead>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {periods.isLoading ? (
              <TableMessageRow colSpan={4}>Loading…</TableMessageRow>
            ) : rows.length === 0 ? (
              <TableMessageRow colSpan={4}>
                {status || year ? (
                  <p className="font-medium">No months match these filters.</p>
                ) : (
                  <>
                    <p className="font-medium">No month has been run yet.</p>
                    {canRun && (
                      <p className="mx-auto mt-1 max-w-lg text-sm">
                        <strong>Generate payroll</strong> above makes the month and calculates it in
                        one go.
                      </p>
                    )}
                  </>
                )}
              </TableMessageRow>
            ) : (
              rows.map((period) => (
                <TableRow
                  key={period.id}
                  data-state={period.id === selectedId ? "selected" : undefined}
                >
                  <TableCell>
                    {/* Filters the payslips above to this month, which is the
                        only thing a month's name is useful for here. */}
                    <button
                      type="button"
                      className="font-medium tabular-nums hover:underline"
                      onClick={() => onSelect(period.id === selectedId ? "" : period.id)}
                    >
                      {period.label}
                    </button>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {period.period_start} → {period.period_end}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={cn("font-normal", STATUS_TONE[period.status])}
                      variant="secondary"
                    >
                      {CLOSED.includes(period.status) ? <Lock className="mr-1 h-3 w-3" /> : null}
                      {period.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    {canRun && !CLOSED.includes(period.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => calculate.mutate(period)}
                        disabled={calculate.isPending}
                      >
                        <Calculator className="mr-2 h-4 w-4" />
                        Calculate
                      </Button>
                    )}
                    {canRun && period.status === "review" && (
                      <Button size="sm" onClick={() => approve.mutate(period)}>
                        Approve &amp; lock
                      </Button>
                    )}
                    {canRun && ["approved", "paid"].includes(period.status) && (
                      <Button size="sm" variant="outline" onClick={() => setReopening(period)}>
                        <Unlock className="mr-2 h-4 w-4" />
                        Reopen
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          pagination={pager}
          total={periods.data?.total}
          loading={periods.isLoading}
          unit="month"
        />
      </ListPageTable>

      {reopening && (
        <ReopenDialog
          period={reopening}
          onClose={() => setReopening(null)}
          onReopened={() => {
            setReopening(null);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

/** The three returns each month owes, and whether they have been made. */
function ReturnsSection({
  periodId,
  onSelectPeriod,
}: {
  periodId: string;
  onSelectPeriod: (id: string) => void;
}) {
  const [authority, setAuthority] = useState("");
  const [status, setStatus] = useState("");
  const sort = useListSort({ column: "due_date", ascending: true });
  const filters = { periodId, authority, status, sort: sort.sort };
  const pager = usePagination({ resetKey: filters });

  const filings = useQuery({
    queryKey: ["payroll_filings", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const list = api.payrollFilings
        .list()
        .order(sort.sort.column, { ascending: sort.sort.ascending });
      // A month's three returns can share a due date and a status, so ties fall
      // back to the date and then the authority, and a page break stays put.
      if (sort.sort.column !== "due_date") list.order("due_date");
      if (sort.sort.column !== "authority") list.order("authority");
      if (periodId) list.eq("pay_period_id", periodId);
      if (authority) list.eq("authority", authority);
      /*
       * "Overdue" is not a status any return is stored as — it is what "due"
       * means once the due date has passed, which is how the table colours it.
       * Sending it as an `eq` matched nothing and read as "no returns are
       * overdue", so it is translated into the two conditions it stands for.
       */
      if (status === "overdue") {
        list.eq("status", "due").lt("due_date", new Date().toISOString().slice(0, 10));
      } else if (status) {
        list.eq("status", status);
      }
      const { data, error, count } = await list.page(pager.page, pager.pageSize);
      if (error) throw new Error(error.message);
      return { rows: (data ?? []) as PayrollFiling[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const rows = filings.data?.rows ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const [editing, setEditing] = useState<PayrollFiling | null>(null);

  /*
   * The files behind each return on the page, one request apiece.
   *
   * A month owes three returns, so this is three small calls rather than a
   * count denormalised onto the filing — which would have to be maintained on
   * every upload and delete, and would be the field that goes stale. The same
   * query key backs the dialog's list, so opening a return costs nothing more.
   */
  const documents = useQueries({
    queries: rows.map((filing) => filingDocumentsQuery(filing.id)),
  });
  const documentCount = new Map(
    rows.map((filing, index) => [filing.id, documents[index]?.data?.length ?? 0]),
  );

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-2">
      <h2 className="shrink-0 text-sm font-semibold">Returns</h2>
      <p className="text-muted-foreground shrink-0 text-sm">
        Both halves of each contribution, which is what is paid over — the employee's share having
        been withheld rather than collected separately. Filing and paying are tracked separately,
        because either one can be the one that is late.
      </p>

      {/*
       * The month is the same choice the Payslips tab makes, deliberately: a
       * month picked to read its payslips is the month whose returns you then
       * want, and two independent pickers for one question is how they end up
       * disagreeing.
       */}
      <ListToolbar className="mb-2 shrink-0">
        <PayPeriodSelect
          id="return-month"
          value={periodId}
          onChange={onSelectPeriod}
          clearable
          clearLabel="Any month"
          className="w-full sm:w-56"
        />
        <EnumFilter
          id="return-authority"
          label="Authority"
          value={authority}
          onChange={setAuthority}
          anyLabel="Every authority"
          options={FILING_AUTHORITIES.map((value) => ({
            value,
            label: AUTHORITY_LABEL[value] ?? titleCase(value),
          }))}
          className="w-full sm:w-56"
        />
        <EnumFilter
          id="return-status"
          label="Status"
          value={status}
          onChange={setStatus}
          anyLabel="Any status"
          options={FILING_STATUSES.map((value) => ({
            value,
            label: FILING_STATUS_LABEL[value] ?? titleCase(value),
          }))}
          className="w-full sm:w-40"
        />
        {(periodId || authority || status) && (
          <Button
            variant="ghost"
            onClick={() => {
              onSelectPeriod("");
              setAuthority("");
              setStatus("");
            }}
          >
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        )}
      </ListToolbar>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <TableHead className="w-8" />
              <SortHeader sort={sort} column="authority">
                Authority
              </SortHeader>
              <SortHeader sort={sort} column="due_date">
                Due
              </SortHeader>
              <SortHeader sort={sort} column="employee_total" className="text-right" align="right">
                Employee
              </SortHeader>
              <SortHeader sort={sort} column="employer_total" className="text-right" align="right">
                Employer
              </SortHeader>
              <SortHeader sort={sort} column="total" className="text-right" align="right">
                Total
              </SortHeader>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              <SortHeader sort={sort} column="filed_on">
                Filed
              </SortHeader>
              <SortHeader sort={sort} column="paid_on">
                Paid
              </SortHeader>
              {/* Counted from the documents read per row, not a column of the return. */}
              <TableHead className="text-right">Files</TableHead>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filings.isLoading ? (
              <TableMessageRow colSpan={RETURN_COLUMNS}>Loading…</TableMessageRow>
            ) : rows.length === 0 ? (
              <TableMessageRow colSpan={RETURN_COLUMNS}>
                {periodId || authority || status
                  ? "No returns match these filters."
                  : "Returns appear once a month has been calculated."}
              </TableMessageRow>
            ) : (
              rows.map((filing) => (
                <ReturnRows
                  key={filing.id}
                  filing={filing}
                  today={today}
                  files={documentCount.get(filing.id) ?? 0}
                  onEdit={() => setEditing(filing)}
                />
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          pagination={pager}
          total={filings.data?.total}
          loading={filings.isLoading}
          unit="return"
        />
      </ListPageTable>

      {editing && <ReturnDialog filing={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

/** Chevron, the six figures, the three states, the file count, the edit button. */
const RETURN_COLUMNS = 11;

/** The statuses a person sets. `overdue` is not one of them — see below. */
const SETTABLE_FILING_STATUSES = ["due", "filed", "paid"] as const;

/**
 * One return: the row, and the files attached to it underneath.
 *
 * The files are kept here rather than behind the edit dialog because they are
 * evidence, and evidence that has to be gone looking for does not get looked
 * at. The count is on the row at every status, and opening the row shows what
 * the count is of — so a return marked paid with nothing attached is visibly
 * somebody's word for it, which is the thing worth noticing.
 *
 * The row opens at any status, including `due`. Attaching the submitted form
 * before the payment clears, or correspondence to a return still being argued
 * about, is the ordinary case rather than an edge one.
 */
function ReturnRows({
  filing,
  today,
  files,
  onEdit,
}: {
  filing: PayrollFiling;
  today: string;
  files: number;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const { hasAny } = useAuth();
  const mayWrite = hasAny(PAYROLL_WRITE_ROLES);
  const [open, setOpen] = useState(false);

  const authority = AUTHORITY_LABEL[filing.authority] ?? filing.authority;
  const overdue = filing.status === "due" && filing.due_date < today;
  const unpaidLate = !filing.paid_on && filing.due_date < today;

  /*
   * Changing the status writes the date it is a claim about along with it, and
   * sends both explicitly. The API would fill a missing date with today, which
   * is right for "we filed it just now" and wrong for re-marking a return that
   * was filed in September — so the dates the row already holds are sent rather
   * than left for the server to guess at.
   */
  const setStatus = useMutation({
    mutationFn: async (next: (typeof SETTABLE_FILING_STATUSES)[number]) => {
      const patch =
        next === "due"
          ? { status: next, filed_on: null, paid_on: null }
          : next === "filed"
            ? { status: next, filed_on: filing.filed_on ?? today, paid_on: null }
            : {
                status: next,
                filed_on: filing.filed_on ?? today,
                paid_on: filing.paid_on ?? today,
              };

      const result = await api.payrollFilings.update(filing.id, patch);
      if (result.error) throw new Error(result.error.message);
      return next;
    },
    onSuccess: (next) => {
      notify.success(`${authority} marked ${FILING_STATUS_LABEL[next]?.toLowerCase() ?? next}`);
      void qc.invalidateQueries({ queryKey: ["payroll_filings"] });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <Fragment>
      <TableRow>
        <TableCell className="p-0 pl-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} the files attached to the ${authority} return`}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell>{authority}</TableCell>
        <TableCell
          className={cn("tabular-nums", overdue && "font-semibold text-red-600 dark:text-red-400")}
        >
          {filing.due_date}
        </TableCell>
        <TableCell className="text-right tabular-nums">{money(filing.employee_total)}</TableCell>
        <TableCell className="text-right tabular-nums">{money(filing.employer_total)}</TableCell>
        <TableCell className="text-right font-semibold tabular-nums">
          {money(filing.total)}
        </TableCell>

        {/*
         * Set here rather than only in the dialog, because marking a return
         * paid is the thing this table is read to do.
         *
         * `overdue` is absent from the options on purpose: it is not a state
         * anybody puts a return into, it is what "due" means once the due date
         * has passed. It is worked out from the date — which is why the date
         * beside it goes red — and offering it would let somebody set a return
         * to a status the next change would silently discard.
         */}
        <TableCell>
          {mayWrite ? (
            <Select
              value={filing.status === "overdue" ? "due" : filing.status}
              onValueChange={(next) =>
                setStatus.mutate(next as (typeof SETTABLE_FILING_STATUSES)[number])
              }
              disabled={setStatus.isPending}
            >
              <SelectTrigger
                className={cn(
                  "h-8 w-[108px]",
                  overdue && "border-red-400 text-red-700 dark:border-red-500/60 dark:text-red-300",
                )}
                aria-label={`Status of the ${authority} return`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SETTABLE_FILING_STATUSES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {FILING_STATUS_LABEL[option] ?? titleCase(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant={overdue ? "destructive" : "secondary"}>
              {overdue ? "overdue" : filing.status}
            </Badge>
          )}
        </TableCell>

        <TableCell className="tabular-nums">{filing.filed_on ?? "—"}</TableCell>
        {/*
         * A return past its due date with no payment against it is the state
         * that costs money quietly, so it is named rather than left as an em
         * dash like any other blank.
         */}
        <TableCell
          className={cn(
            "tabular-nums",
            unpaidLate && "font-semibold text-red-600 dark:text-red-400",
          )}
        >
          {filing.paid_on ?? (unpaidLate ? "Unpaid" : "—")}
        </TableCell>

        <TableCell className="text-right">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 font-normal tabular-nums"
            onClick={() => setOpen((value) => !value)}
            aria-label={`${open ? "Hide" : "Show"} the ${files} file(s) on the ${authority} return`}
          >
            <Paperclip className="h-3.5 w-3.5" />
            {files > 0 ? files : <span className="text-muted-foreground">0</span>}
          </Button>
        </TableCell>

        <TableCell>
          <RowActions>
            <RowAction
              label={`Edit the dates and references on the ${authority} return`}
              onClick={onEdit}
            >
              <Pencil />
            </RowAction>
          </RowActions>
        </TableCell>
      </TableRow>

      {open && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell />
          <TableCell colSpan={RETURN_COLUMNS - 1} className="py-4">
            <FilingDocuments filingId={filing.id} authority={authority} mayWrite={mayWrite} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

/**
 * Every file attached to one return, whatever the return's status.
 *
 * Reads the same query the count on the row reads, so opening a return costs no
 * request and the two can never disagree about how many there are.
 */
function FilingDocuments({
  filingId,
  authority,
  mayWrite,
}: {
  filingId: string;
  authority: string;
  mayWrite: boolean;
}) {
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [attaching, setAttaching] = useState(false);

  const documents = useQuery(filingDocumentsQuery(filingId));

  const open = useMutation({
    mutationFn: async (documentId: string) => {
      const result = await api.payrollFilings.documentUrl(filingId, documentId);
      if (result.error || !result.data) {
        throw new Error(result.error?.message ?? "That file could not be opened");
      }
      window.open(result.data.url, "_blank", "noopener,noreferrer");
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const remove = useMutation({
    mutationFn: async (documentId: string) => {
      const result = await api.payrollFilings.removeDocument(filingId, documentId);
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("File removed");
      void qc.invalidateQueries({ queryKey: ["payroll_filing_documents", filingId] });
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Files on the {authority} return</h3>
        {mayWrite && !attaching && (
          <Button variant="outline" size="sm" onClick={() => setAttaching(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Attach a file
          </Button>
        )}
      </div>

      {documents.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : documents.error ? (
        <p className="text-destructive text-sm">{(documents.error as Error).message}</p>
      ) : (documents.data ?? []).length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing attached yet — the submitted return, the authority's acknowledgement and the proof
          of payment all belong here.
        </p>
      ) : (
        <ul className="divide-y rounded-md border bg-background">
          {(documents.data ?? []).map((document) => (
            <li key={document.id} className="flex items-center gap-2 px-3 py-2">
              <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{document.title}</div>
                <div className="text-muted-foreground truncate text-xs">
                  {FILING_DOCUMENT_KIND_LABEL[document.kind] ?? titleCase(document.kind)} ·{" "}
                  {document.file_name} · {fileSize(document.size_bytes)} ·{" "}
                  {formatDate(document.created_at)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => open.mutate(document.id)}
                disabled={open.isPending}
                aria-label={`Open ${document.title}`}
              >
                <Download className="h-4 w-4" />
              </Button>
              {mayWrite && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    const ok = await confirmDelete(
                      document.title,
                      "The file is removed from storage as well, and cannot be recovered.",
                    );
                    if (ok) remove.mutate(document.id);
                  }}
                  aria-label={`Remove ${document.title}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {attaching && (
        <AttachFileForm
          filingId={filingId}
          onCancel={() => setAttaching(false)}
          onUploaded={() => {
            setAttaching(false);
            void qc.invalidateQueries({ queryKey: ["payroll_filing_documents", filingId] });
          }}
        />
      )}
    </div>
  );
}

/** One return's files, shared by the table's count and the dialog's list. */
function filingDocumentsQuery(filingId: string) {
  return {
    queryKey: ["payroll_filing_documents", filingId],
    queryFn: async () => {
      const result = await api.payrollFilings.documents(filingId);
      if (result.error) throw new Error(result.error.message);
      return (result.data ?? []) as PayrollFilingDocument[];
    },
  };
}

/**
 * The dates and references behind one return.
 *
 * Filing and paying are two dates rather than one status, because they happen
 * weeks apart and either can be the one that slipped. A status is still stored
 * — the filters and the row read it — but it is derived from the dates here
 * rather than set beside them, so the two cannot come to disagree. That is also
 * why all of it saves in one request: a status sent without its dates is how a
 * row ends up reading "paid" with nothing to say when.
 *
 * The status select on the row is the same field from the other end: it sets a
 * status and writes the matching date. Either way round the invariant holds —
 * the status is always answerable with a date.
 *
 * The files are on the row rather than in here. Evidence that has to be gone
 * looking for does not get looked at, and a dialog is somewhere you go.
 *
 * The amounts are deliberately absent. They are the run's output, and a figure
 * on a return that could be typed over is a reconciliation of nothing — a
 * correction belongs in a recalculation of the period, or as an adjustment on
 * the next one.
 */
function ReturnDialog({ filing, onClose }: { filing: PayrollFiling; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasAny } = useAuth();
  const mayWrite = hasAny(PAYROLL_WRITE_ROLES);

  const [filedOn, setFiledOn] = useState(filing.filed_on ?? "");
  const [paidOn, setPaidOn] = useState(filing.paid_on ?? "");
  const [reference, setReference] = useState(filing.reference ?? "");
  const [paymentReference, setPaymentReference] = useState(filing.payment_reference ?? "");
  const [notes, setNotes] = useState(filing.notes ?? "");

  const save = useMutation({
    mutationFn: async () => {
      if (paidOn && filedOn && paidOn < filedOn) {
        throw new Error("The payment date is before the filing date");
      }
      const result = await api.payrollFilings.update(filing.id, {
        // Paid implies filed, which is why the status walks down from the later
        // of the two dates rather than being a field of its own on this form.
        status: paidOn ? "paid" : filedOn ? "filed" : "due",
        filed_on: filedOn || null,
        paid_on: paidOn || null,
        reference: reference.trim() || null,
        payment_reference: paymentReference.trim() || null,
        notes: notes.trim() || null,
      });
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Return updated");
      void qc.invalidateQueries({ queryKey: ["payroll_filings"] });
      onClose();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  const authority = AUTHORITY_LABEL[filing.authority] ?? filing.authority;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{authority}</DialogTitle>
          <DialogDescription>
            {money(filing.total)} due {filing.due_date} — {money(filing.employee_total)} withheld
            from pay and {money(filing.employer_total)} the employer's own.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-5 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="filing-filed-on">Filed on</Label>
              <Input
                id="filing-filed-on"
                type="date"
                value={filedOn}
                disabled={!mayWrite}
                onChange={(e) => setFiledOn(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                The date the return went to the authority.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="filing-paid-on">Paid on</Label>
              <Input
                id="filing-paid-on"
                type="date"
                value={paidOn}
                disabled={!mayWrite}
                onChange={(e) => setPaidOn(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                The date the money left. Leave it empty until it has.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="filing-reference">Return reference</Label>
              <Input
                id="filing-reference"
                value={reference}
                disabled={!mayWrite}
                onChange={(e) => setReference(e.target.value)}
                placeholder="The authority's own reference"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="filing-payment-reference">Payment reference</Label>
              <Input
                id="filing-payment-reference"
                value={paymentReference}
                disabled={!mayWrite}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Cheque number, wire reference"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="filing-notes">Notes</Label>
            <Textarea
              id="filing-notes"
              rows={2}
              value={notes}
              disabled={!mayWrite}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {mayWrite ? "Cancel" : "Close"}
          </Button>
          {mayWrite && (
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Attaching one file to a return.
 *
 * Inline rather than a second dialog, so the list it adds to stays visible —
 * and so the save button behind it is never hidden by something stacked on top
 * of it. The bytes go straight from the browser to storage and the row is
 * registered afterwards, which is what keeps the list above free of files that
 * cannot be downloaded.
 */
function AttachFileForm({
  filingId,
  onCancel,
  onUploaded,
}: {
  filingId: string;
  onCancel: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState<FilingDocumentKind>("return");
  const [title, setTitle] = useState("");

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Pick a file first");
      const result = await api.payrollFilings.uploadDocument(filingId, file, {
        kind,
        // The filename is a reasonable title and a poor one; it is offered as a
        // default so nobody has to type "NHI return" twice.
        title: title.trim() || file.name,
      });
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("File attached");
      onUploaded();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <div className="bg-muted/40 space-y-3 rounded-md border p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="filing-doc-file" required>
            File
          </Label>
          <Input
            id="filing-doc-file"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.tif,.tiff,.doc,.docx,.xls,.xlsx,.txt"
            onChange={(e) => {
              const picked = e.target.files?.[0] ?? null;
              setFile(picked);
              if (picked && !title.trim()) setTitle(picked.name.replace(/\.[^.]+$/, ""));
            }}
          />
          <p className="text-muted-foreground text-xs">PDF, image, Word or Excel, up to 25MB.</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="filing-doc-kind">What it is</Label>
          <Select value={kind} onValueChange={(next) => setKind(next as FilingDocumentKind)}>
            <SelectTrigger id="filing-doc-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILING_DOCUMENT_KINDS.map((option) => (
                <SelectItem key={option} value={option}>
                  {FILING_DOCUMENT_KIND_LABEL[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="filing-doc-title">Title</Label>
        <Input
          id="filing-doc-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What this file is"
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => upload.mutate()} disabled={upload.isPending || !file}>
          {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Generating a month's payroll.
 *
 * One month, named rather than inferred: the month is made if it does not exist
 * yet and then calculated, which is the two steps the old screen asked for
 * separately. Calculating is idempotent — running it again replaces that
 * month's payslips rather than adding to them — so pressing this on a month
 * already in review is how a correction on the grid gets picked up.
 *
 * A month that has been approved or filed is refused by the server rather than
 * quietly reopened, and the refusal says which.
 */
/**
 * The year's payroll as a workbook, gathered from the payslips when it is asked
 * for — so a month generated or recalculated a minute ago is already in it.
 *
 * Asks for the year, and, while the table is narrowed to one employee, whether
 * the report is that employee's or everybody's: the file does not say which set
 * it holds once it is open.
 */
function PayrollReportDialog({ employeeId, onClose }: { employeeId: string; onClose: () => void }) {
  const [year, setYear] = useState<number | null>(null);
  const [scope, setScope] = useState<"employee" | "all">(employeeId ? "employee" : "all");
  const [busy, setBusy] = useState(false);

  const years = useQuery({
    queryKey: ["pay_period_years"],
    queryFn: async () => {
      const { data, error } = await api.payPeriods.list().select("year").page(1, MAX_PAGE_SIZE);
      if (error) throw new Error(error.message);
      const seen = new Set((data ?? []).map((row) => Number((row as { year: number }).year)));
      return [...seen].filter(Number.isFinite).sort((a, b) => b - a);
    },
  });
  const chosenYear = year ?? years.data?.[0] ?? null;

  /** How many payslips each choice would write, so the choice is an informed one. */
  const counts = useQueries({
    queries: (employeeId ? [employeeId, ""] : [""]).map((who) => ({
      queryKey: ["payslips", "report-count", chosenYear, who],
      enabled: chosenYear != null,
      queryFn: async () => {
        const { error, count } = await api.payslips.list({
          year: chosenYear ?? undefined,
          employeeId: who || undefined,
          pageSize: 1,
        });
        if (error) throw new Error(error.message);
        return count ?? 0;
      },
    })),
  });
  const employeeCount = employeeId ? counts[0]?.data : undefined;
  const allCount = counts[employeeId ? 1 : 0]?.data;
  const countLabel = (n: number | undefined) =>
    n == null ? "" : ` (${n.toLocaleString()} payslip${n === 1 ? "" : "s"})`;

  const run = async () => {
    if (chosenYear == null) return;
    setBusy(true);
    try {
      const who = scope === "employee" ? employeeId : undefined;
      const slips: PayslipRow[] = [];
      for (let page = 1; ; page++) {
        const { data, error, count } = await api.payslips.list({
          year: chosenYear,
          employeeId: who,
          page,
          pageSize: MAX_PAGE_SIZE,
        });
        if (error) throw new Error(error.message);
        slips.push(...(data ?? []));
        if ((data ?? []).length < MAX_PAGE_SIZE || slips.length >= (count ?? 0)) break;
      }
      if (slips.length === 0) {
        notify.info("Nothing to export", {
          description: `No payslips have been generated for ${chosenYear} yet.`,
        });
        return;
      }
      downloadPayrollReport(chosenYear, slips, who ? `-${slugOf(slips[0].employee_name)}` : "");
      const months = new Set(slips.map((slip) => slip.pay_period_id)).size;
      notify.success(`Exported ${chosenYear}`, {
        description: `${slips.length.toLocaleString()} payslips across ${months} month${months === 1 ? "" : "s"}.`,
      });
      onClose();
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Payroll report</DialogTitle>
          <DialogDescription>
            A row per month and a block per employee — gross, payroll tax, NHI and SSB split
            employer and employee, net — with a column for every bonus, loan repayment or other pay
            item the payslips carried. Built from the payslips as they stand now, so a month you
            have just generated is already in it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <section className="space-y-2">
            <Label className="text-muted-foreground" htmlFor="payroll-report-year">
              Year
            </Label>
            <Select
              value={chosenYear != null ? String(chosenYear) : ""}
              onValueChange={(value) => setYear(Number(value))}
              disabled={!years.data?.length}
            >
              <SelectTrigger id="payroll-report-year" className="w-full">
                <SelectValue placeholder={years.isLoading ? "Loading…" : "No pay periods yet"} />
              </SelectTrigger>
              <SelectContent>
                {(years.data ?? []).map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          {employeeId ? (
            <section className="space-y-2">
              <Label className="text-muted-foreground">Which employees</Label>
              <RadioGroup
                value={scope}
                onValueChange={(value) => setScope(value as "employee" | "all")}
                className="gap-3"
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    id="payroll-report-employee"
                    value="employee"
                    className="mt-0.5"
                  />
                  <Label htmlFor="payroll-report-employee" className="font-normal">
                    The employee the table is filtered to{countLabel(employeeCount)}
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem id="payroll-report-all" value="all" className="mt-0.5" />
                  <Label htmlFor="payroll-report-all" className="font-normal">
                    Everyone{countLabel(allCount)}
                  </Label>
                </div>
              </RadioGroup>
            </section>
          ) : (
            <p className="text-sm text-muted-foreground">
              Everyone on the payroll{countLabel(allCount)}.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={run} disabled={busy || chosenYear == null}>
            <Download className="mr-2 h-4 w-4" />
            {busy ? "Exporting…" : "Download .xlsx"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function slugOf(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Generating a month, as three steps in one dialog: which month and when it is
 * paid, who it is for, and whether their attendance is complete.
 *
 * The third step is where a run used to fail. The server refuses over any
 * unanswered half-day, and that refusal arrived as an error on the page behind
 * a closed dialog. Now the dialog reads the same list before the run, for the
 * people chosen, and they are marked there — so Generate is offered only once
 * there is nothing left for the server to refuse over.
 */
function GenerateDialog({
  onClose,
  onRan,
}: {
  onClose: () => void;
  onRan: (run: PayRunResult) => void;
}) {
  const now = new Date();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);

  /*
   * The day the money lands, which the payslip prints and nothing else derives:
   * a month's period ends on its last day, and the transfer goes out whenever
   * the business sends it. Null until somebody types one, so the field can show
   * the date the period already carries without a state that fights the query.
   */
  const [payDateTyped, setPayDateTyped] = useState<string | null>(null);
  const today = now.toISOString().slice(0, 10);

  // What the month is called — `Sep-2026` — and separately the `YYYY-MM` stem
  // its dates are built from. They were one string while the name happened to
  // be the stem; a name is not a date and no longer pretends to be one.
  const label = payPeriodLabel(year, month);
  const stem = `${year}-${String(month).padStart(2, "0")}`;

  // The month the period covers, which is what the sequence means at a monthly
  // frequency. `Date.UTC(year, month, 0)` is the last day of `month`.
  const periodStart = `${stem}-01`;
  const periodEnd = `${stem}-${String(new Date(Date.UTC(year, month, 0)).getUTCDate()).padStart(2, "0")}`;

  /** Whether this month already exists, and in what state. */
  const existing = useQuery({
    queryKey: ["pay_period_for", year, month],
    queryFn: async () => {
      const { data, error } = await api.payPeriods
        .list()
        .eq("year", year)
        .eq("sequence", month)
        .limit(1);
      if (error) throw new Error(error.message);
      return ((data ?? []) as PayPeriod[])[0] ?? null;
    },
  });

  // What the field shows: what has been typed, else what the period already
  // carries, else today — the day most payrolls are run for.
  const payDate = payDateTyped ?? existing.data?.pay_date ?? today;

  /*
   * Who the month is run for. Only active employees employed at some point in
   * the month are offered — the same people the server would take — and all of
   * them start ticked, so the ordinary run is one click. What is kept is who was
   * *unticked*, so somebody who appears after a refetch arrives ticked too.
   */
  const people = useQuery({
    queryKey: ["people"],
    queryFn: async () => {
      const result = await api.employees.people();
      if (result.error) throw new Error(result.error.message);
      return result;
    },
  });
  const eligible = useMemo(
    () =>
      (people.data?.people ?? [])
        .filter(
          (p: Person) =>
            p.status === "active" &&
            (!p.start_date || p.start_date <= periodEnd) &&
            (!p.end_date || p.end_date >= periodStart),
        )
        .sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [people.data, periodStart, periodEnd],
  );
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set());
  const [personSearch, setPersonSearch] = useState("");
  const chosen = eligible.filter((p) => !unticked.has(p.id));
  const allChosen = chosen.length === eligible.length;
  /*
   * Which rows the table shows: everybody by default, or only the ticked or
   * unticked ones — on a long register, "who have I left out?" is a question
   * worth one click rather than a scroll.
   */
  const [personFilter, setPersonFilter] = useState<"all" | "selected" | "unselected">("all");
  const shown = useMemo(() => {
    const needle = personSearch.trim().toLowerCase();
    return eligible.filter((p) => {
      if (personFilter === "selected" && unticked.has(p.id)) return false;
      if (personFilter === "unselected" && !unticked.has(p.id)) return false;
      if (!needle) return true;
      return [p.full_name, p.employee_no, p.job_title]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(needle));
    });
  }, [eligible, personSearch, personFilter, unticked]);

  /*
   * The open days of the people chosen, read once step 3 is reached. Same query
   * the table inside reads, so marking a stretch there updates the count here.
   */
  const chosenIds = useMemo(() => new Set(chosen.map((p) => p.id)), [chosen]);
  const unanswered = useUnansweredDays(periodStart, periodEnd, step === 3);
  const openPeople = useMemo(
    () =>
      new Set(
        (unanswered.data ?? [])
          .filter((d) => chosenIds.has(d.employee_id))
          .map((d) => d.employee_id),
      ),
    [unanswered.data, chosenIds],
  );

  const tick = (id: string, on: boolean) =>
    setUnticked((prev) => {
      const next = new Set(prev);
      if (on) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = useMutation({
    mutationFn: async () => {
      let period = existing.data ?? null;

      if (!period) {
        const created = await api.payPeriods.create({
          year,
          sequence: month,
          label,
          period_start: periodStart,
          period_end: periodEnd,
          pay_date: payDate,
        });
        if (created.error) throw new Error(created.error.message);
        period = created.data as PayPeriod;
      } else if (period.pay_date !== payDate) {
        // Generating a month again is also how a pay date is corrected: the
        // payslip reads it from the period when the document is drawn, so a
        // correction here shows on every payslip of the month, reprints
        // included.
        const moved = await api.payPeriods.update(period.id, { pay_date: payDate });
        if (moved.error) throw new Error(moved.error.message);
        period = moved.data as PayPeriod;
      }

      // Everybody ticked is sent as no list at all, which the server reads as
      // every active employee — so a person added a moment ago is not missed.
      const result = await api.payPeriods.calculate(period.id, {
        employeeIds: allChosen ? undefined : chosen.map((p) => p.id),
      });
      if (result.error) throw runError(result.error);
      return result.data as PayRunResult;
    },
    onSuccess: (data) => {
      notify.success(`${label} calculated — ${data.totals.headcount} payslip(s).`);
      onRan(data);
    },
    onError: (error: Error & { code?: string }) => {
      // Somebody's day went back to open between the check and the run: the
      // list on this step is re-read, and the dialog stays where it is.
      if (error.code === "unanswered_days") {
        void unanswered.refetch();
        return notify.error("Some days are still unanswered — mark them below, then generate.");
      }
      notify.error(error.message);
    },
  });

  const closed = existing.data && CLOSED.includes(existing.data.status);

  const STEPS = ["Month", "Employees", "Attendance"] as const;
  const wide = step === 3 && openPeople.size > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "flex max-h-[90vh] flex-col",
          wide ? "sm:max-w-5xl" : step === 2 ? "sm:max-w-3xl" : "sm:max-w-2xl",
        )}
      >
        <DialogHeader>
          <DialogTitle>Generate payroll</DialogTitle>
          <DialogDescription>
            Reads the attendance grid for the month, applies each contract and the current rates,
            and writes a payslip per employee.
          </DialogDescription>
        </DialogHeader>

        {/* Where you are, and a way back to any step already passed. */}
        <ol className="flex items-center gap-2 text-sm">
          {STEPS.map((name, index) => {
            const number = (index + 1) as 1 | 2 | 3;
            const done = number < step;
            const current = number === step;
            return (
              <li key={name} className="flex items-center gap-2">
                {index > 0 && <span className="bg-border h-px w-6 sm:w-10" aria-hidden />}
                <button
                  type="button"
                  disabled={!done}
                  onClick={() => setStep(number)}
                  aria-current={current ? "step" : undefined}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-1 py-0.5",
                    done && "hover:bg-muted cursor-pointer",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-full border text-xs font-medium",
                      current && "border-primary bg-primary text-primary-foreground",
                      done && "border-primary text-primary",
                      !current && !done && "text-muted-foreground",
                    )}
                  >
                    {done ? <Check className="h-3.5 w-3.5" /> : number}
                  </span>
                  <span className={cn(!current && "text-muted-foreground")}>{name}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-0.5">
          {step === 1 && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="generate-month">Month</Label>
                  <Select value={String(month)} onValueChange={(value) => setMonth(Number(value))}>
                    <SelectTrigger id="generate-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((name, index) => (
                        <SelectItem key={name} value={String(index + 1)}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="generate-year" required>
                    Year
                  </Label>
                  <Input
                    id="generate-year"
                    type="number"
                    value={year}
                    onChange={(event) => setYear(Number(event.target.value))}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="generate-pay-date" required>
                    Pay date
                  </Label>
                  <Input
                    id="generate-pay-date"
                    type="date"
                    value={payDate}
                    onChange={(event) => setPayDateTyped(event.target.value)}
                  />
                  <p className="text-muted-foreground text-xs">
                    The day the money lands. Printed on every payslip of the month, and changed by
                    generating again with another date.
                  </p>
                </div>
              </div>

              <details className="rounded-md border p-3">
                <summary className="cursor-pointer text-sm font-medium">
                  Payslip letterhead
                  <span className="text-muted-foreground font-normal">
                    {" "}
                    — the address on the document
                  </span>
                </summary>
                {/*
                 * Here as well as in Payroll settings because this is where
                 * somebody is standing when they notice it is wrong: about to run
                 * a month, not reading settings. Saving is its own button — it
                 * does not run the payroll, and generating does not save it.
                 */}
                <div className="pt-3">
                  <PayslipLetterheadEditor />
                </div>
              </details>

              <p className="text-muted-foreground text-sm">
                {existing.isPending
                  ? "Checking whether this month has been run…"
                  : closed
                    ? `${label} is ${existing.data?.status} and cannot be recalculated. Reopen it ` +
                      "first, under Months."
                    : existing.data
                      ? `${label} already exists and is ${existing.data.status}. Generating ` +
                        "replaces its payslips with a fresh calculation."
                      : `${label} will be created, covering ${periodStart} to ${periodEnd}.`}
              </p>
            </>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Employees</Label>
                <span className="text-muted-foreground text-xs">
                  {people.isPending
                    ? "Reading the register…"
                    : `${chosen.length} of ${eligible.length} selected`}
                </span>
              </div>
              {people.error ? (
                <p className="text-destructive text-sm">{(people.error as Error).message}</p>
              ) : !people.isPending && eligible.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nobody active was employed during {label}.
                </p>
              ) : (
                <div className="flex min-h-0 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      value={personSearch}
                      onChange={(event) => setPersonSearch(event.target.value)}
                      placeholder="Search by name, number or title…"
                      aria-label="Search employees"
                      className="h-8 min-w-48 flex-1"
                    />
                    <Select
                      value={personFilter}
                      onValueChange={(value) => setPersonFilter(value as typeof personFilter)}
                    >
                      <SelectTrigger className="h-8 w-40" aria-label="Show">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All ({eligible.length})</SelectItem>
                        <SelectItem value="selected">Selected ({chosen.length})</SelectItem>
                        <SelectItem value="unselected">
                          Not selected ({eligible.length - chosen.length})
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Table containerClassName="max-h-[45vh] rounded-md border">
                    <TableHeader sticky>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            id="generate-all"
                            aria-label="Select every employee"
                            checked={
                              allChosen ? true : chosen.length === 0 ? false : "indeterminate"
                            }
                            onCheckedChange={(on) =>
                              setUnticked(
                                on === true ? new Set() : new Set(eligible.map((p) => p.id)),
                              )
                            }
                            disabled={people.isPending}
                          />
                        </TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>No.</TableHead>
                        <TableHead>Job title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Contract</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {people.isPending && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-muted-foreground text-center">
                            Reading the register…
                          </TableCell>
                        </TableRow>
                      )}
                      {shown.map((p) => {
                        const ticked = !unticked.has(p.id);
                        return (
                          <TableRow
                            key={p.id}
                            data-state={ticked ? "selected" : undefined}
                            className="cursor-pointer"
                            onClick={() => tick(p.id, !ticked)}
                          >
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <Checkbox
                                aria-label={`Include ${p.full_name}`}
                                checked={ticked}
                                onCheckedChange={(on) => tick(p.id, on === true)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{p.full_name}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {p.employee_no ?? "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground max-w-48 truncate">
                              {p.job_title ?? "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {EMPLOYMENT_LABEL[p.employment_type] ?? p.employment_type}
                            </TableCell>
                            <TableCell className="text-right">
                              {p.contract === null ? (
                                <Badge variant="outline" className="text-[10px]">
                                  no contract
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">On file</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!people.isPending && shown.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-muted-foreground text-center">
                            {personSearch ? `Nobody matches “${personSearch}”.` : "Nobody here."}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
              <p className="text-muted-foreground text-xs">
                Only active employees are listed. Somebody on leave, suspended or gone is not paid
                by the run — set them back to active first if they should be.
              </p>
              {existing.data && !allChosen && (
                <p className="text-sm">
                  {label} already has payslips. Generating replaces them, so anybody unticked is
                  left without a payslip for the month.
                </p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="flex min-h-0 flex-col gap-3">
              <p className="text-muted-foreground text-sm">
                {label} · {periodStart} to {periodEnd} · paid {payDate} · {chosen.length}{" "}
                {chosen.length === 1 ? "employee" : "employees"}
              </p>

              {unanswered.isPending ? (
                <p className="text-muted-foreground text-sm">Checking the attendance grid…</p>
              ) : unanswered.error ? (
                <p className="text-destructive text-sm">{(unanswered.error as Error).message}</p>
              ) : openPeople.size > 0 ? (
                <>
                  <Alert variant="destructive">
                    <HelpCircle className="h-4 w-4" />
                    <AlertTitle>Some days have not been answered</AlertTitle>
                    <AlertDescription>
                      {openPeople.size} {openPeople.size === 1 ? "person has" : "people have"} days
                      with no answer. A day with no record is not the same as a day somebody was
                      absent, so the run will not guess — mark them below, then generate.
                    </AlertDescription>
                  </Alert>
                  <UnansweredDaysTable
                    from={periodStart}
                    to={periodEnd}
                    label={label}
                    only={chosenIds}
                    emptyMessage=""
                  />
                </>
              ) : (
                <Alert>
                  <Check className="h-4 w-4" />
                  <AlertTitle>Attendance is complete</AlertTitle>
                  <AlertDescription>
                    Every working day of {label} has an answer for the people chosen.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 1 ? (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setStep((step - 1) as 1 | 2)}>
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              onClick={() => setStep(2)}
              disabled={existing.isPending || Boolean(closed) || !payDate || !year}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <Button onClick={() => setStep(3)} disabled={people.isPending || chosen.length === 0}>
              Next
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={() => run.mutate()}
              disabled={
                run.isPending ||
                unanswered.isPending ||
                !!unanswered.error ||
                openPeople.size > 0 ||
                chosen.length === 0 ||
                Boolean(closed)
              }
            >
              {run.isPending ? "Generating…" : `Generate ${label}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reopening a locked month.
 *
 * Requires a reason, which is appended to the period's notes — reopening a
 * month that has been approved changes figures somebody has already signed off,
 * and the record of why should outlive whoever did it. Refused outright once any
 * return has been filed.
 */
function ReopenDialog({
  period,
  onClose,
  onReopened,
}: {
  period: PayPeriod;
  onClose: () => void;
  onReopened: () => void;
}) {
  const [reason, setReason] = useState("");

  const reopen = useMutation({
    mutationFn: async () => {
      const result = await api.payPeriods.reopen(period.id, reason.trim());
      if (result.error) throw new Error(result.error.message);
    },
    onSuccess: () => {
      notify.success("Month reopened and its attendance days released");
      onReopened();
    },
    onError: (error: Error) => notify.error(error.message),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen {period.label}?</DialogTitle>
          <DialogDescription>
            This releases the attendance days so the month can be corrected and recalculated. It is
            refused if any return for the period has already been filed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="reopen-reason" required>
            Why
          </Label>
          <Textarea
            id="reopen-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            placeholder="What was wrong with the figures as approved"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => reopen.mutate()} disabled={reopen.isPending || !reason.trim()}>
            Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Keeps the server's code on the error, which is what `unanswered_days` turns on. */
/** A run refused over unanswered days: what the server said, and the month it was for. */
interface UnansweredRun {
  message: string;
  from: string;
  to: string;
  label: string;
}

function runError(error: { message: string; code?: string }): Error & { code?: string } {
  const out = new Error(error.message) as Error & { code?: string };
  out.code = error.code;
  return out;
}
